// src/pages/Documents/EditDocumentSummary.js
import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Typography,
  Stack,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { marked } from "marked";

// Tiptap Imports
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";

// Import TiptapEditor component
import TiptapEditor from "../../components/Common/TiptapEditor";

export default function EditDocumentSummary({
  document,
  onClose,
  onUpdate,
  chatbotId,
  permission,
}) {
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("save");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "tiptap-editor-area p-3 form-control",
      },
    },
  });

  useEffect(() => {
    if (document && editor) {
      const summaryAsHtml = document.summary
        ? marked.parse(document.summary)
        : "<p></p>";
      if (editor.getHTML() !== summaryAsHtml) {
        editor.commands.setContent(summaryAsHtml, false);
      }
    }
  }, [document, editor]);

  const handleSave = async () => {
    if (!editor) return;

    setIsSaving(true);
    setSaveStatus("saving");
    setError("");
    setSuccess("");

    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};

      if (!document?.id) throw new Error("Document ID is missing.");

      const payload = {
        id: document.id,
        summary: editor.getHTML(),
      };

      const response = await axiosInstance.post(
        `/edit-document-summary`,
        payload,
        { params }
      );

      if (response.status === 200 && response.data.success) {
        setSaveStatus("saved");
        setSuccess("Summary updated successfully!");
        setTimeout(() => {
          onUpdate();
        }, 1200);
      } else {
        throw new Error(
          response.data.message || `Failed to update summary.`
        );
      }
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to update summary.";
      setError(message);
      setSaveStatus("save");
    } finally {
      setIsSaving(false);
    }
  };

  if (!editor) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ p: 2 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="textPrimary">
            Edit Summary
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {document?.filename}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={isSaving}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Divider />

      {/* Use TiptapEditor component */}
      <Box sx={{ flexShrink: 0 }}>
        <TiptapEditor editor={editor} label="Document Summary" height="300px" />
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: "auto", p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
          A brief summary of the document's content. This will be saved and
          will update the document's metadata for the chatbot.
        </Typography>
      </Box>

      <Divider />

      <Stack direction="row" spacing={2} sx={{ p: 2 }}>
        {permission.canModify && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={
              isSaving ? (
                <CircularProgress size={20} />
              ) : saveStatus === "saved" ? (
                <i className="fa fa-check" />
              ) : (
                <SaveIcon />
              )
            }
            fullWidth
          >
            {saveStatus === "saving"
              ? "Saving..."
              : saveStatus === "saved"
              ? "Saved!"
              : "Save Summary"}
          </Button>
        )}
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          disabled={isSaving}
          fullWidth
        >
          Cancel
        </Button>
      </Stack>

      {!permission?.canModify && (
        <Alert severity="warning" sx={{ m: 2 }}>
          You don't have permission to edit document summaries.
        </Alert>
      )}
    </Box>
  );
}